import { useState, useEffect, useCallback, useRef } from 'react';
import { IonPage, IonContent } from '@ionic/react';
import { useLocation } from 'react-router-dom';
import { DataTable } from '../components/shared/DataTable';
import { searchProfiles, setSlackActivityNotify } from '../services/profileService';
import { getBusinessById, resendInvitation, getNotificationLink, suspendProfile, activateProfile, removeProfile } from '../services/businessService';
import { getClientById } from '../services/clientService';
import { impersonateProfile } from '../services/authService';
import { SetUserTrialModal } from '../components/profiles/SetUserTrialModal';
import { SlackActivityToggle } from '../components/shared/SlackActivityToggle';
import { RoleFilterModal } from '../components/profiles/RoleFilterModal';
import { ManageRolesModal } from '../components/profiles/ManageRolesModal';
import { InviteTeamAdminModal } from '../components/teams/InviteTeamAdminModal';
import { ManageSeatsModal } from '../components/teams/ManageSeatsModal';
import { ConfirmProfileActionModal, type ProfileAction } from '../components/profiles/ConfirmProfileActionModal';
import { RemovedProfilesModal } from '../components/profiles/RemovedProfilesModal';
import type { Business } from '../types/business';
import { useBreadcrumbStore } from '../store/useBreadcrumbStore';
import { useProfileStore } from '../store/useProfileStore';
import { useRefresh } from '../contexts/RefreshContext';
import type { Profile, UserTrial } from '../types/profile';
import { isAccountAdmin, canManageBusinessProfiles, isProtectedBusinessOwner } from '../types/profile';
import type { Column } from '../components/shared/DataTable';

const ALL_ROLES = ['owner', 'admin', 'user', 'agent', 'shop_owner', 'shop_admin', 'shop_user', 'client_owner', 'client_admin'];

const ROLE_PRIORITY: Record<string, number> = { client_owner: -1, owner: 0, client_admin: 0, admin: 1, user: 2 };

function profilePriority(roles?: string[]): number {
  if (!roles?.length) return 4;
  const best = Math.min(...roles.map((r) => ROLE_PRIORITY[r] ?? Infinity));
  if (best < Infinity) return best;
  if (roles.includes('agent')) return 3;
  return 4;
}

function formatTrialDate(iso: string, timezone?: string): string {
  try {
    return new Intl.DateTimeFormat('en-US', {
      month: '2-digit', day: '2-digit', year: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: true,
      timeZone: timezone || undefined,
    }).format(new Date(iso));
  } catch { return iso; }
}

function RowMenu({ showManageRoles, showResendInvitation, showImpersonate, showProfileActions, profileStatus, isProtectedOwner, businessId, profileId, slackActivity, onManageRoles, onSetTrial, onToggleSlackActivity, onResendInvitation, onGetNotificationLink, onImpersonate, onProfileAction }: {
  showManageRoles: boolean;
  showResendInvitation: boolean;
  showImpersonate: boolean;
  showProfileActions: boolean;
  profileStatus: string;
  isProtectedOwner: boolean;
  businessId: string;
  profileId: string;
  slackActivity: boolean;
  onManageRoles: () => void;
  onSetTrial: () => void;
  onToggleSlackActivity: () => void;
  onResendInvitation: () => void;
  onGetNotificationLink: () => void;
  onImpersonate: () => void;
  onProfileAction: (action: ProfileAction) => void;
}) {
  const [open, setOpen] = useState(false);
  const [hasLink, setHasLink] = useState<boolean | null>(null); // null = not checked yet
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  useEffect(() => {
    if (!open || !showResendInvitation) return;
    setHasLink(null);
    getNotificationLink(businessId, profileId)
      .then((res) => setHasLink(!!res))
      .catch(() => setHasLink(false));
  }, [open, showResendInvitation, businessId, profileId]);

  const canSuspend = showProfileActions && !isProtectedOwner && (profileStatus === 'active' || profileStatus === 'pending');
  const canActivate = showProfileActions && profileStatus === 'suspended';
  const canRemove = showProfileActions && !isProtectedOwner && profileStatus !== 'deleted';

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="p-1 text-gray-400 hover:text-gray-700 rounded hover:bg-gray-100 transition-colors"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="12" cy="5" r="2" />
          <circle cx="12" cy="12" r="2" />
          <circle cx="12" cy="19" r="2" />
        </svg>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1 min-w-[180px]">
          {showManageRoles && (
            <button
              onClick={() => { setOpen(false); onManageRoles(); }}
              className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
            >
              Manage Shop Roles
            </button>
          )}
          <button
            onClick={() => { setOpen(false); onSetTrial(); }}
            className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
          >
            Set User Trial
          </button>
          <div
            className="px-3 py-2 flex items-center justify-between gap-3"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="text-sm text-gray-700">Slack Activity</span>
            <SlackActivityToggle
              checked={slackActivity}
              showLabel={false}
              onChange={onToggleSlackActivity}
            />
          </div>
          {showResendInvitation && (
            <>
              <button
                onClick={() => { setOpen(false); onResendInvitation(); }}
                className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
              >
                {hasLink === false ? 'Send Invitation' : 'Resend Invitation'}
              </button>
              <button
                onClick={() => { setOpen(false); onGetNotificationLink(); }}
                disabled={hasLink === false || hasLink === null}
                className={`w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 ${hasLink === false || hasLink === null ? 'text-gray-300 cursor-not-allowed' : 'text-gray-700'}`}
              >
                Get Notification Link
              </button>
            </>
          )}
          {showImpersonate && (
            <button
              onClick={() => { setOpen(false); onImpersonate(); }}
              className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
            >
              Impersonate User
            </button>
          )}
          {canSuspend && (
            <button
              onClick={() => { setOpen(false); onProfileAction('suspend'); }}
              className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
            >
              Suspend
            </button>
          )}
          {canActivate && (
            <button
              onClick={() => { setOpen(false); onProfileAction('activate'); }}
              className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
            >
              Activate
            </button>
          )}
          {canRemove && (
            <button
              onClick={() => { setOpen(false); onProfileAction('remove'); }}
              className="w-full text-left px-3 py-1.5 text-sm text-red-600 hover:bg-gray-50"
            >
              Remove
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function ProfilesPage() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const businessId = params.get('businessId') || undefined;
  const clientId = params.get('clientId') || undefined;

  const [data, setData] = useState<Profile[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(true);
  const { activeProfile } = useProfileStore();
  const [trialProfile, setTrialProfile] = useState<{ id: string; trials: UserTrial[] } | null>(null);
  const [manageRolesProfile, setManageRolesProfile] = useState<Profile | null>(null);
  const [business, setBusiness] = useState<Business | null>(null);
  const [roleFilter, setRoleFilter] = useState<Set<string>>(new Set(ALL_ROLES));
  const [showRoleFilter, setShowRoleFilter] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [resendProfile, setResendProfile] = useState<{ businessId: string; profileId: string } | null>(null);
  const [resendLoading, setResendLoading] = useState(false);
  const [notificationLinkProfile, setNotificationLinkProfile] = useState<{ businessId: string; profileId: string } | null>(null);
  const [notificationLink, setNotificationLink] = useState('');
  const [notificationLinkLoading, setNotificationLinkLoading] = useState(false);
  const [notificationLinkCopied, setNotificationLinkCopied] = useState(false);
  const [impersonateProfileId, setImpersonateProfileId] = useState<string | null>(null);
  const [impersonateLoading, setImpersonateLoading] = useState(false);
  const [manageMenuOpen, setManageMenuOpen] = useState(false);
  const [inviteAdminBusinessId, setInviteAdminBusinessId] = useState<string | null>(null);
  const [manageSeatsBusinessId, setManageSeatsBusinessId] = useState<string | null>(null);
  const [showRemovedProfiles, setShowRemovedProfiles] = useState(false);
  const [profileAction, setProfileAction] = useState<{
    action: ProfileAction;
    businessId: string;
    profileId: string;
    displayName: string;
  } | null>(null);
  const [profileActionLoading, setProfileActionLoading] = useState(false);
  const manageRef = useRef<HTMLDivElement>(null);
  const setBreadcrumb = useBreadcrumbStore((s) => s.set);
  const canManageProfiles = canManageBusinessProfiles(activeProfile);

  useEffect(() => {
    if (location.pathname !== '/profiles') return;
    if (businessId && clientId) {
      Promise.all([getBusinessById(businessId), getClientById(clientId)]).then(([biz, client]) => {
        setBusiness(biz);
        setBreadcrumb([
          { label: 'Clients', path: '/clients' },
          { label: client.name || clientId, path: `/client-detail?clientId=${clientId}` },
          { label: 'Teams', path: `/teams?clientId=${clientId}` },
          { label: 'Users' },
        ]);
      });
    } else if (businessId) {
      getBusinessById(businessId).then((biz) => {
        setBusiness(biz);
        setBreadcrumb([
          { label: 'Teams', path: '/teams' },
          { label: biz.name || businessId },
          { label: 'Users' },
        ]);
      });
    } else {
      setBusiness(null);
      setBreadcrumb([{ label: 'Users' }]);
    }
  }, [location.pathname, businessId, clientId, setBreadcrumb]);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const filter = { persona: 'business' as const, ...(businessId ? { businessId } : {}) };
      const results = await searchProfiles(filter, { page, pageSize, orderBy: 'updated', order: 'desc', expand: 'business,userPlan,lastAuthentication' });
      setData(results);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, businessId, refreshKey]);

  useEffect(() => { fetch(); }, [fetch]);

  const { registerRefreshHandler, unregisterRefreshHandler } = useRefresh();
  useEffect(() => {
    registerRefreshHandler('/profiles', fetch);
    return () => unregisterRefreshHandler('/profiles');
  }, [fetch, registerRefreshHandler, unregisterRefreshHandler]);

  useEffect(() => {
    if (!manageMenuOpen) return;
    function handleClick(e: MouseEvent) {
      if (manageRef.current && !manageRef.current.contains(e.target as Node)) setManageMenuOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [manageMenuOpen]);

  const columns: Column<Profile>[] = [
    { key: 'business', label: 'Team', render: (item) => item.business?.name || '—' },
    { key: 'displayName', label: 'Display Name' },
    { key: 'email', label: 'Email' },
    {
      key: 'status',
      label: 'Status',
      render: (item) => {
        if (item.status === 'suspended') return <span className="text-orange-600">Suspended</span>;
        if (item.status === 'deleted') return <span className="text-gray-400">Removed</span>;
        if (item.status === 'pending') return <span className="italic text-orange-500">Pending</span>;
        return 'Active';
      },
    },
    {
      key: 'roles',
      label: 'Roles',
      render: (item) => {
        if (!item.roles?.length) return '—';
        const roles = item.roles;
        const hasClientOwner = roles.includes('client_owner');
        const hasClientAdmin = roles.includes('client_admin');
        const hasOwner = roles.includes('owner');
        const hasAdmin = roles.includes('admin');
        const hasPropertyId = !!(item.business as { propertyId?: string } | undefined)?.propertyId;

        const chips: { label: string; bg: string; text: string; border?: string }[] = [];

        if (hasClientOwner) {
          chips.push({ label: 'Owner', bg: 'var(--ion-color-primary)', text: '#fff' });
        } else if (hasClientAdmin) {
          chips.push({ label: 'Admin', bg: 'var(--ion-color-primary)', text: '#fff' });
        } else {
          if (hasOwner) {
            chips.push({
              label: hasPropertyId ? 'Property Owner' : 'Team Owner',
              bg: 'var(--ion-color-secondary)', text: '#fff',
            });
          }
          if (hasAdmin) {
            chips.push({
              label: hasPropertyId ? 'Property Admin' : 'Team Admin',
              bg: 'var(--ion-color-secondary)', text: '#fff',
            });
          }
        }

        if (roles.includes('shop_admin')) {
          chips.push({ label: 'Shop Admin', bg: 'var(--ion-color-secondary)', text: '#fff' });
        }
        if (roles.includes('shop_user')) {
          chips.push({ label: 'View Shops', bg: 'var(--ion-color-tertiary)', text: '#fff' });
        }
        // shop_owner: no chip shown

        if (roles.includes('user') && !hasOwner && !hasAdmin && !hasClientOwner && !hasClientAdmin) {
          chips.push({ label: 'User', bg: 'var(--ion-color-tertiary)', text: '#fff' });
        }
        if (roles.includes('agent') && !hasOwner && !hasAdmin && !hasClientOwner && !hasClientAdmin) {
          chips.push({ label: 'Agent', bg: '#fff', text: '#374151', border: '1px solid #d1d5db' });
        }

        return chips.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {chips.map((chip, i) => (
              <span
                key={i}
                className="px-2 py-0.5 rounded-full text-xs font-medium"
                style={{ backgroundColor: chip.bg, color: chip.text, border: chip.border }}
              >
                {chip.label}
              </span>
            ))}
          </div>
        ) : '—';
      },
    },
    {
      key: 'lastAuth',
      label: 'Last Auth',
      render: (item) => item.lastAuthentication
        ? formatTrialDate(item.lastAuthentication, activeProfile?.timezone)
        : '—',
    },
    {
      key: 'trial',
      label: 'Trial',
      render: (item) => {
        const trials = item.userPlan?.trial;
        if (!trials?.length) return '—';
        return trials.map((t) => formatTrialDate(t.expires, activeProfile?.timezone)).join(', ');
      },
    },
    ...(business?.demo ? [{
      key: 'demo',
      label: 'Demo',
      render: (item: Profile) => item.business?.demo ? '✓' : '',
    }] : []),
  ];

  const filterActive = roleFilter.size < ALL_ROLES.length;
  const filteredData = (filterActive
    ? data.filter((p) => p.roles?.some((r) => roleFilter.has(r)))
    : [...data]
  ).sort((a, b) => profilePriority(a.roles) - profilePriority(b.roles));

  return (
    <IonPage>
      <IonContent>
        <div className="p-4 sm:p-6 lg:p-8">
          <DataTable
            title={business?.name ? `Users - ${business.name}` : 'Users'}
            columns={columns}
            data={filteredData}
            page={page}
            pageSize={pageSize}
            loading={loading}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
            onRefresh={fetch}
            onFilter={() => setShowRoleFilter(true)}
            filterActive={filterActive}
            headerControls={businessId ? (
              <div className="flex items-center gap-2">
                <span
                  className="px-2 py-0.5 rounded-full text-xs font-medium"
                  style={business?.demo
                    ? { backgroundColor: 'var(--ion-color-dark)', color: '#fff' }
                    : { backgroundColor: '#fff', color: '#9ca3af', border: '1px solid #d1d5db' }
                  }
                >
                  Demo
                </span>
                <div ref={manageRef} className="relative">
                  <button
                    onClick={() => setManageMenuOpen(!manageMenuOpen)}
                    className="px-2 py-1 text-xs font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Manage
                  </button>
                  {manageMenuOpen && (
                    <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1 min-w-[180px]">
                      <button
                        onClick={() => { setManageMenuOpen(false); setInviteAdminBusinessId(businessId); }}
                        className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        Invite Team Admin
                      </button>
                      <button
                        onClick={() => { setManageMenuOpen(false); setManageSeatsBusinessId(businessId); }}
                        className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        Manage LMS Seats
                      </button>
                      {canManageProfiles && (
                        <button
                          onClick={() => { setManageMenuOpen(false); setShowRemovedProfiles(true); }}
                          className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          Removed Users
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : undefined}
            rowMenu={(item) => {
              const isAgentOnly = item.roles?.includes('agent') && !item.roles.some((r) => r === 'owner' || r === 'admin' || r === 'user');
              if (isAgentOnly) return null;
              return (
                <RowMenu
                  showManageRoles={item.roles?.some((r) => r === 'owner' || r === 'admin' || r === 'user') ?? false}
                  showResendInvitation={item.status === 'pending' && (item.roles?.some((r) => r === 'owner' || r === 'admin' || r === 'user') ?? false)}
                  showImpersonate={isAccountAdmin(activeProfile)}
                  showProfileActions={canManageProfiles}
                  profileStatus={item.status}
                  isProtectedOwner={isProtectedBusinessOwner(item.roles)}
                  businessId={item.businessId}
                  profileId={item.id}
                  slackActivity={!!item.metadata?.slackActivity}
                  onManageRoles={() => setManageRolesProfile(item)}
                  onSetTrial={() => setTrialProfile({ id: item.id, trials: item.userPlan?.trial || [] })}
                  onToggleSlackActivity={async () => {
                    const enabled = !item.metadata?.slackActivity;
                    setData((prev) => prev.map((p) => (
                      p.id === item.id
                        ? { ...p, metadata: { ...p.metadata, slackActivity: enabled } }
                        : p
                    )));
                    try {
                      await setSlackActivityNotify(item.id, enabled);
                    } catch {
                      await fetch();
                    }
                  }}
                  onResendInvitation={() => setResendProfile({ businessId: item.businessId, profileId: item.id })}
                  onGetNotificationLink={() => {
                    setNotificationLink('');
                    setNotificationLinkCopied(false);
                    setNotificationLinkProfile({ businessId: item.businessId, profileId: item.id });
                    setNotificationLinkLoading(true);
                    getNotificationLink(item.businessId, item.id)
                      .then((res) => setNotificationLink(res?.link || 'Failed to get link'))
                      .catch(() => setNotificationLink('Failed to get link'))
                      .finally(() => setNotificationLinkLoading(false));
                  }}
                  onImpersonate={() => setImpersonateProfileId(item.id)}
                  onProfileAction={(action) => setProfileAction({
                    action,
                    businessId: item.businessId,
                    profileId: item.id,
                    displayName: item.displayName,
                  })}
                />
              );
            }}
          />

          {showRoleFilter && (
            <RoleFilterModal
              selected={roleFilter}
              onApply={(roles) => { setRoleFilter(roles); setShowRoleFilter(false); setPage(1); setRefreshKey((k) => k + 1); }}
              onClose={() => setShowRoleFilter(false)}
            />
          )}

          {manageRolesProfile && (
            <ManageRolesModal
              businessId={manageRolesProfile.businessId}
              profileId={manageRolesProfile.id}
              currentRoles={manageRolesProfile.roles}
              onClose={() => setManageRolesProfile(null)}
              onSuccess={async () => { setManageRolesProfile(null); await fetch(); }}
            />
          )}

          {inviteAdminBusinessId && (
            <InviteTeamAdminModal
              businessId={inviteAdminBusinessId}
              onClose={() => setInviteAdminBusinessId(null)}
              onSuccess={() => { setInviteAdminBusinessId(null); fetch(); }}
            />
          )}

          {manageSeatsBusinessId && (
            <ManageSeatsModal
              businessId={manageSeatsBusinessId}
              onClose={() => setManageSeatsBusinessId(null)}
              onSuccess={async () => { setManageSeatsBusinessId(null); await fetch(); }}
            />
          )}

          {trialProfile && (
            <SetUserTrialModal
              profileId={trialProfile.id}
              trials={trialProfile.trials}
              onClose={() => setTrialProfile(null)}
              onSuccess={async () => {
                setTrialProfile(null);
                await fetch();
              }}
            />
          )}

          {resendProfile && (
            <div className="fixed inset-0 z-50 flex items-center justify-center">
              <div className="absolute inset-0 bg-black/40" onClick={() => setResendProfile(null)} />
              <div className="relative bg-white rounded-xl shadow-lg w-full max-w-sm mx-4">
                <div className="px-6 pt-6 pb-4">
                  <h2 className="text-lg font-bold text-gray-900 mb-2">Resend Invitation</h2>
                  <p className="text-sm text-gray-600">Click to confirm resend of invitation email</p>
                </div>
                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => setResendProfile(null)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={resendLoading}
                    onClick={async () => {
                      setResendLoading(true);
                      try {
                        await resendInvitation(resendProfile.businessId, resendProfile.profileId);
                        setResendProfile(null);
                      } catch {
                        // error handled by apiService
                      } finally {
                        setResendLoading(false);
                      }
                    }}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {resendLoading ? 'Sending...' : 'Confirm'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {notificationLinkProfile && (
            <div className="fixed inset-0 z-50 flex items-center justify-center">
              <div className="absolute inset-0 bg-black/40" onClick={() => setNotificationLinkProfile(null)} />
              <div className="relative bg-white rounded-xl shadow-lg w-full max-w-md mx-4">
                <div className="px-6 pt-6 pb-4">
                  <h2 className="text-lg font-bold text-gray-900 mb-3">Get Notification Link</h2>
                  {notificationLinkLoading ? (
                    <p className="text-sm text-gray-400">Loading...</p>
                  ) : (
                    <div className="flex items-start gap-2">
                      <textarea
                        readOnly
                        rows={5}
                        value={notificationLink}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(notificationLink);
                          setNotificationLinkCopied(true);
                          setTimeout(() => setNotificationLinkCopied(false), 2000);
                        }}
                        className="shrink-0 px-3 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                        title="Copy to clipboard"
                      >
                        {notificationLinkCopied ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-end px-6 py-4 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => setNotificationLinkProfile(null)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}

          {impersonateProfileId && (
            <div className="fixed inset-0 z-50 flex items-center justify-center">
              <div className="absolute inset-0 bg-black/40" onClick={() => setImpersonateProfileId(null)} />
              <div className="relative bg-white rounded-xl shadow-lg w-full max-w-sm mx-4">
                <div className="px-6 pt-6 pb-4">
                  <h2 className="text-lg font-bold text-gray-900 mb-2">Impersonate User</h2>
                  <p className="text-sm text-gray-600">Click to confirm impersonation of this user in the app.</p>
                </div>
                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => setImpersonateProfileId(null)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={impersonateLoading}
                    onClick={async () => {
                      setImpersonateLoading(true);
                      try {
                        const { secret } = await impersonateProfile(impersonateProfileId);
                        const appBaseUrl = import.meta.env.VITE_APP_BASE_URL || '';
                        window.open(`${appBaseUrl}?impersonate=${secret}`, '_blank');
                        setImpersonateProfileId(null);
                      } catch {
                        // error handled by apiService
                      } finally {
                        setImpersonateLoading(false);
                      }
                    }}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {impersonateLoading ? 'Loading...' : 'Confirm'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {profileAction && (
            <ConfirmProfileActionModal
              action={profileAction.action}
              displayName={profileAction.displayName}
              contextLabel="team"
              loading={profileActionLoading}
              onClose={() => setProfileAction(null)}
              onConfirm={async () => {
                setProfileActionLoading(true);
                try {
                  const { action, businessId: bizId, profileId } = profileAction;
                  if (action === 'suspend') await suspendProfile(bizId, profileId);
                  if (action === 'activate') await activateProfile(bizId, profileId);
                  if (action === 'remove') await removeProfile(bizId, profileId);
                  setProfileAction(null);
                  await fetch();
                } catch {
                  // error handled by apiService
                } finally {
                  setProfileActionLoading(false);
                }
              }}
            />
          )}

          {showRemovedProfiles && businessId && (
            <RemovedProfilesModal
              businessId={businessId}
              title={business?.name ? `Removed Users - ${business.name}` : 'Removed Users'}
              contextLabel="team"
              onClose={() => setShowRemovedProfiles(false)}
              onSuccess={fetch}
            />
          )}
        </div>
      </IonContent>
    </IonPage>
  );
}
